import { t } from "i18next";
import { useCallback, useState } from "react";
import { useRecoilState } from "recoil";

import {
  CrudApi,
  IAreaDto,
  IGeoJSON,
  IShownAreaPolygon,
  IStationDto,
  Log,
  routeColorsByIdMapping,
  routeColorsIds,
  shownAreaPolygonsAtom,
} from "@ryde/shared";

/** Lowest route-palette color id not already used by a shown polygon (cycles when exhausted). */
const pickColorId = (usedColorIds: number[]): number => {
  const ids = routeColorsIds();
  const used = new Set(usedColorIds);
  const free = ids.find(id => !used.has(id));

  return free ?? ids[usedColorIds.length % ids.length];
};

export const useStationsAreaPolygons = () => {
  const [shownPolygons, setShownPolygons] = useRecoilState(
    shownAreaPolygonsAtom,
  );
  const [loadingAreaId, setLoadingAreaId] = useState<number | null>(null);

  const getShownPolygon = useCallback(
    (areaId?: number): IShownAreaPolygon | undefined =>
      areaId ? shownPolygons[areaId] : undefined,
    [shownPolygons],
  );

  const hidePolygon = useCallback(
    (areaId: number): void => {
      setShownPolygons(prev => {
        const next = { ...prev };
        delete next[areaId];

        return next;
      });
    },
    [setShownPolygons],
  );

  const showPolygon = useCallback(
    async (areaId: number, areaName: string): Promise<void> => {
      if (shownPolygons[areaId]) {
        return;
      }

      setLoadingAreaId(areaId);

      try {
        const area = (await CrudApi.getById("area", areaId)) as
          IAreaDto | undefined;
        const polygon = area?.metadata?.polygon as IGeoJSON | undefined;

        if (!polygon?.features?.length) {
          Log.infoAndToast(t("station.mapAreaHasNoPolygon"));

          return;
        }

        setShownPolygons(prev => {
          if (prev[areaId]) {
            return prev;
          }

          const colorId = pickColorId(
            Object.values(prev).map(item => item.colorId),
          );

          return {
            ...prev,
            [areaId]: {
              areaId,
              areaName,
              value: polygon,
              colorId,
              colorValue: routeColorsByIdMapping[colorId],
            },
          };
        });
      } finally {
        setLoadingAreaId(null);
      }
    },
    [shownPolygons, setShownPolygons],
  );

  const togglePolygon = useCallback(
    (station: IStationDto): void => {
      const areaId = station.areaId;

      if (!areaId) {
        return;
      }

      if (shownPolygons[areaId]) {
        hidePolygon(areaId);

        return;
      }

      void showPolygon(areaId, station.areaName ?? "");
    },
    [shownPolygons, hidePolygon, showPolygon],
  );

  return {
    getShownPolygon,
    togglePolygon,
    loadingAreaId,
  };
};
